import React, { useMemo } from "react";
import "./InitialsAvatar.css";

// --- Color Palette (Vibrant, accessible colors) ---
const AVATAR_COLORS = [
  "#ef4444", // Red
  "#f97316", // Orange
  "#f59e0b", // Amber
  "#84cc16", // Lime
  "#10b981", // Emerald
  "#06b6d4", // Cyan
  "#3b82f6", // Blue
  "#6366f1", // Indigo
  "#8b5cf6", // Violet
  "#d946ef", // Fuchsia
  "#f43f5e", // Rose
];

/**
 * Extracts 1-2 character initials from a name string.
 * e.g., "John Doe" -> "JD", "Alice" -> "AL", "J" -> "J"
 */
function getInitials(name: string | null | undefined): string {
  if (!name || typeof name !== "string") return "?";
  
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  
  // Case 1: "John Doe" -> "JD"
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  
  // Case 2: "John" -> "JO"
  if (parts[0].length >= 2) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  
  // Case 3: "J" -> "J"
  return parts[0][0].toUpperCase();
}

/**
 * Generates a consistent color based on the name string hash.
 */
function getColorByName(name: string | null | undefined): string {
  if (!name) return "#9ca3af"; // Gray fallback for unknown
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash % AVATAR_COLORS.length);
  return AVATAR_COLORS[index];
}

export interface InitialsAvatarProps {
  name?: string | null;
  src?: string | null; 
  size?: number;   // Optional explicit size in pixels
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
}

export default function InitialsAvatar({ 
  name, 
  src,
  size, 
  className = "", 
  style = {},
  onClick 
}: InitialsAvatarProps) {
  
  const initials = useMemo(() => getInitials(name), [name]);
  const backgroundColor = useMemo(() => getColorByName(name), [name]);

  // Combine dynamic styles (Size prop overrides CSS)
  const finalStyle: React.CSSProperties = {
    backgroundColor,
    ...style,
    ...(size ? { 
      width: size, 
      height: size, 
      minWidth: size, 
      minHeight: size, 
      fontSize: Math.max(12, size * 0.4) // Scale font relative to size
    } : {}),
  };

  if (src) {
    return (
      <img 
        src={src} 
        alt={name || "Avatar"} 
        className={`initials-avatar ${className}`.trim()}
        style={{ ...finalStyle, objectFit: 'cover', backgroundColor: 'transparent' }}
        onClick={onClick}
      />
    );
  }

  return (
    <div 
      className={`initials-avatar ${className}`.trim()} 
      style={finalStyle}
      aria-hidden="true"
      title={name || "User Avatar"}
      onClick={onClick}
    >
      {initials}
    </div>
  );
}